import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { listWorkspaces, buildAccountOptions } from "@/lib/collaborators";
import { friendlyError } from "@/lib/errors";
import { SWITCHER } from "@/constants/testIds/profile";
import { Check, Eye, Loader2, UserRound, Store, RotateCcw } from "lucide-react";
import { toast } from "sonner";

// Shared account switcher (Epic 10.3). Lists the user's own account plus any
// shared workspaces they collaborate on, and switches context on tap. Rendered
// in three places (bottom sheet, desktop header menu, profile page) — `dense`
// tightens spacing for the dropdown. Degrades to own-account-only when the
// /collaborators/workspaces endpoint is not live yet (graceful 404).
export default function AccountSwitcher({ dense = false, onSwitched, title = "Trocar de conta" }) {
  const { user, switchWorkspace } = useAuth();
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [busyId, setBusyId] = useState(undefined); // option key currently switching

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const { data } = await listWorkspaces();
      setWorkspaces(data?.workspaces || (Array.isArray(data) ? data : []));
    } catch (err) {
      setWorkspaces([]);
      // Endpoint not live yet (404) stays silent — own account still works.
      // Any other failure surfaces a discreet inline retry.
      if (err?.response?.status !== 404) setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const options = buildAccountOptions(user, workspaces);
  const keyOf = (opt) => (opt.id == null ? "own" : String(opt.id));

  const select = async (opt) => {
    if (opt.active || busyId !== undefined) return;
    setBusyId(keyOf(opt));
    try {
      await switchWorkspace(opt.id);
      toast.success(opt.kind === "own" ? "De volta à sua conta ✦" : `Vendo ${opt.label} ✦`);
      onSwitched?.(opt);
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setBusyId(undefined);
    }
  };

  // Only-own-account and endpoint not live: keep the section minimal.
  const hasShared = options.some((o) => o.kind === "shared");

  return (
    <div data-testid={SWITCHER.root}>
      {(hasShared || loading) && (
        <div className={`text-[10.5px] tracking-[0.2em] uppercase text-brand-ink3 font-ui ${dense ? "px-2 py-1" : "mb-2"}`}>
          {title}
        </div>
      )}
      <div className={dense ? "space-y-0.5" : "space-y-1.5"}>
        {options.map((opt) => {
          const key = keyOf(opt);
          const Icon = opt.kind === "own" ? UserRound : Store;
          const busy = busyId === key;
          return (
            <button
              key={key}
              type="button"
              data-testid={`${SWITCHER.option}-${key}`}
              onClick={() => select(opt)}
              disabled={opt.active || busyId !== undefined}
              aria-current={opt.active ? "true" : undefined}
              className={`w-full flex items-center gap-3 min-h-[44px] rounded-2xl px-3 py-2.5 text-left transition-colors ${
                opt.active
                  ? "bg-brand-rose/10 border border-brand-rose/40"
                  : "bg-brand-elevated border border-brand-line hover:border-brand-rose"
              } ${busyId !== undefined && !busy ? "opacity-50" : ""}`}
            >
              <span className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                opt.active ? "bg-brand-rose text-white" : "bg-brand-surface text-brand-ink2"
              }`}>
                <Icon className="w-4 h-4" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-1.5">
                  <span className="text-[14px] text-brand-ink font-ui truncate">{opt.label}</span>
                  {opt.kind === "shared" && (
                    <span className="shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-brand-elevated border border-brand-line text-[10px] text-brand-ink2 font-ui">
                      <Eye className="w-2.5 h-2.5" /> leitura
                    </span>
                  )}
                </span>
                {opt.sublabel && (
                  <span className="block text-[11.5px] text-brand-ink3 font-ui truncate">{opt.sublabel}</span>
                )}
              </span>
              {busy ? (
                <Loader2 className="w-4 h-4 text-brand-rose animate-spin shrink-0" />
              ) : opt.active ? (
                <Check className="w-4 h-4 text-brand-rose shrink-0" />
              ) : null}
            </button>
          );
        })}
      </div>
      {loadError && (
        <div
          data-testid={`${SWITCHER.root}-error`}
          className={`flex items-center gap-2 text-[11.5px] text-brand-ink3 font-ui ${dense ? "px-2 py-1.5" : "mt-2"}`}
        >
          <span className="min-w-0 flex-1">Não consegui carregar as contas compartilhadas.</span>
          <button
            type="button"
            data-testid={`${SWITCHER.root}-retry`}
            onClick={load}
            className="shrink-0 inline-flex items-center gap-1 text-brand-rose hover:underline"
          >
            <RotateCcw className="w-3 h-3" /> Tentar de novo
          </button>
        </div>
      )}
    </div>
  );
}
